/**
 * Error-dialog config and Sentry capture for the policy screen's change/confirm/submit flows (#36445).
 *
 * - A "Refresh page" error carries its own reload action. Before, it fell through to the default
 *   retry, which re-ran handleChangePolicy with `{ id: previewId }` and sent a bogus PolicyInput
 *   ("Variable 'policy' has coerced Null value", STAFF-FE-BJCY/BJCZ).
 * - The capture is a real Error with a stable fingerprint. It used to be a JSON.stringify of the
 *   error plus the whole policy form: a string with no stack is titled `<unknown>`, every event
 *   became its own issue, and the payload carried drivers' DL# and DOB (see AGENTS.md "Logging policy").
 */

export const buildRefreshPageError = ({ message, title }) => ({
  message,
  title,
  label: 'Refresh page',
  tryAgain: () => window.location.reload()
});

// `tryAgain` is the retry a caller passes beside the store result (`{ ...res, tryAgain }`, the
// incident scrub/add previews); `error.tryAgain` is one passed inside the error (segment changes).
export const buildPolicyErrorDialog = ({ error, tryAgain, retryPolicyChange }) => {
  const { message, ...errorRest } = error;
  return {
    messages: message
      ? [message]
      : [
          'Sorry about that. It looks like our systems are not able process a preview for the changes.',
          'Please try again'
        ],
    title: 'Settings preview error',
    label: 'Try Again',
    tryAgain: error?.tryAgain || tryAgain || retryPolicyChange,
    ...errorRest
  };
};

export const buildPolicyErrorCapture = ({ error, action, policyId, previewId }) => {
  const graphQLErrors = (error?.graphQLErrors ?? []).map(({ message, path, errorType }) => ({
    message,
    path,
    errorType
  }));
  const message = graphQLErrors[0]?.message || error?.message || 'Policy change failed';
  return {
    exception: new Error(message),
    captureContext: {
      // Confirm/submit errors have no change action; their dialog title tells them apart from previews
      fingerprint: ['policy-change-error', action ?? error?.title ?? 'preview', message],
      extra: { policyId, previewId, action, graphQLErrors }
    }
  };
};
